import { GMXExpandTileBase } from '../expand-tile-base/expand-tile-base'
import { sanitizeInput, sanitizeUrl } from '../../../../utils/sanitize'
import { dispatchGbaalLinkClickEvent } from '../../../../utils/dispatchGbaalLinkClickEvent'

type WheelTileImgBlobModel = Record<string, Blob>

/**
 * Tile - Colorizer
 */
export class GMXWheelTile extends GMXExpandTileBase {
	wheelSwatchContainerEl: HTMLElement
	wheelSwatchEls: NodeListOf<HTMLInputElement>
	expandedWheelSwatchContainerEl: HTMLElement
	expandedWheelSwatchEls: NodeListOf<HTMLInputElement>
	wheelGroup: HTMLElement
	wheelOverlayImgContainerEl: HTMLElement
	expandedContentContainer: HTMLElement
	tileDescriptionEl: HTMLElement
	wheelSwatchDescription: HTMLElement

	private wheelImgBlobData: WheelTileImgBlobModel = {}

	private activeWheelValue: string | null = null

	private focusRestoreTarget: HTMLInputElement | null = null

	private boundSwatchSelectionHandler = this._handleSwatchSelectionChange.bind(this)
	private boundTileExit = this.tileExit.bind(this)

	connectedCallback(): void {
		super.connectedCallback()

		this.wheelSwatchContainerEl = this.querySelector('.small-wheel-control.wheel-swatches .gmx-controls-vehicle')
		this.expandedWheelSwatchContainerEl = this.querySelector(
			'.large-wheel-control.wheel-swatches .gmx-controls-vehicle'
		)
		this.wheelGroup = this.querySelector('.wheel-group')

		this.expandedContentContainer = this.querySelector('.expanded-content-container')
		this.wheelOverlayImgContainerEl = this.querySelector('.wheel-image-overlay-container')
		this.wheelSwatchDescription = this.querySelector('.wheel-description')
		this.tileDescriptionEl = this.collapsedContentContainerEl.querySelector('.tile-description')

		this.wheelSwatchContainerEl.addEventListener('change', this.boundSwatchSelectionHandler)
		this.wheelSwatchEls = this.wheelSwatchContainerEl.querySelectorAll('input[type="radio"]')
		this.expandedWheelSwatchContainerEl.addEventListener('change', this.boundSwatchSelectionHandler)
		this.expandedWheelSwatchEls = this.expandedWheelSwatchContainerEl.querySelectorAll('input[type="radio"]')
	}

	disconnectedCallback(): void {
		super.disconnectedCallback()
		this.wheelSwatchContainerEl.removeEventListener('change', this.boundSwatchSelectionHandler)
		this.expandedWheelSwatchContainerEl.removeEventListener('change', this.boundSwatchSelectionHandler)
	}

	handleObservationCallback(): void {
		super.handleObservationCallback()

		this.initWheelTile()
	}

	async initWheelTile(): Promise<void> {
		await this._preloadImages()

		// Set default active wheel, not reusing activateWheel to avoid animation on load
		this.wheelSwatchEls.forEach(el => {
			if (el.getAttribute('data-default-active') === 'true') {
				if (el.getAttribute('data-wheel-src')) {
					const wheelOverlayImgEl = this.wheelGroup.querySelector('.wheel-overlay') as HTMLElement
					if (wheelOverlayImgEl) {
						this._loadWheelImageFromBlob(
							el.value,
							el.getAttribute('data-wheel-src'),
							el.getAttribute('data-alt-text'),
							wheelOverlayImgEl
						)
					}
				}
				const wheelDescription = el.getAttribute('data-wheel-desc')
				const wheelDisclosurePath = sanitizeUrl(el.getAttribute('data-wheel-disclosure-path'))

				if (wheelDisclosurePath) {
					this.wheelSwatchDescription.textContent = ''
					this.tileDescriptionEl.textContent = ''

					const newDisclosureSpanEl = document.createElement('span')
					newDisclosureSpanEl.setAttribute('data-legal-uri', wheelDisclosurePath)
					newDisclosureSpanEl.textContent = wheelDescription
					this.wheelSwatchDescription.appendChild(newDisclosureSpanEl)
					this.tileDescriptionEl.appendChild(newDisclosureSpanEl.cloneNode(true))
				} else {
					this.wheelSwatchDescription.textContent = wheelDescription
					this.tileDescriptionEl.textContent = wheelDescription
				}

				const wheel = el.value
				this.wheelSwatchEls.forEach(el => {
					if (el.value === wheel) {
						el.checked = true
					}
				})

				this.expandedWheelSwatchEls.forEach(el => {
					if (el.value === wheel) {
						el.checked = true
					}
				})

				this.activeWheelValue = wheel
			}
		})

		if (this.activeWheelValue === null) {
			const initialChecked =
				this.wheelSwatchContainerEl.querySelector<HTMLInputElement>('input[type="radio"]:checked')
			this.activeWheelValue = initialChecked?.value ?? null
		}
	}

	private async _preloadImages(): Promise<void> {
		const promises: Promise<void>[] = []

		this.wheelSwatchEls.forEach(el => {
			this.wheelImgBlobData[el.value] = this.wheelImgBlobData[el.value] || null
			promises.push(
				fetch(`${el.getAttribute('data-wheel-src')}?imwidth=900`, {
					headers: {
						Accept: 'image/avif,image/webp,image/apng,*/*',
					},
				})
					.then(response => {
						if (!response.ok) {
							window.console.error(
								`ERROR: Wheel Tile.  Response NOT Ok.  Failed to load image for wheel ${el.value}: ${response.status}`
							)
						}
						return response.blob()
					})
					.then(blob => {
						if (blob) {
							this.wheelImgBlobData[el.value] = blob
						}
					})
			)
		})

		await Promise.allSettled(promises)
	}

	handleExpandBtnClicked(e: Event): void {
		const target = e.target as HTMLElement

		if (target?.closest('.small-wheel-control')) {
			return
		}

		if (!(target && (target.hasAttribute('data-toast-uri') || target.closest('[data-toast-uri]')))) {
			this.wheelGroup.classList.remove('enter-tile')
			this.wheelGroup.classList.remove('exit-tile')
			this.expandedContentContainer.appendChild(this.wheelOverlayImgContainerEl)
		}

		super.handleExpandBtnClicked(e)
	}

	handleExpandContentCloseBtnClicked(): void {
		super.handleExpandContentCloseBtnClicked()
		this.wheelGroup.classList.remove('enter-tile')
		this.wheelGroup.classList.remove('exit-tile')
		this.appendChild(this.wheelOverlayImgContainerEl)
	}

	/**
	 * Radio `change` only fires when the selected value actually changes, so re-clicking the active wheel
	 * does not re-run wheel swap / expand-disable logic.
	 */
	private _handleSwatchSelectionChange(e: Event): void {
		e.stopPropagation()

		const input = e.target
		if (!(input instanceof HTMLInputElement) || input.type !== 'radio') {
			return
		}

		const selectedWheel = input.value
		if (selectedWheel) {
			const isNewSelection = selectedWheel !== this.activeWheelValue
			const wheelDescription = input.getAttribute('data-wheel-desc')

			if (isNewSelection) {
				this.focusRestoreTarget = input
			}

			this.activateWheel(
				selectedWheel,
				input.getAttribute('data-wheel-src'),
				wheelDescription,
				input.getAttribute('data-alt-text'),
				input.getAttribute('data-wheel-disclosure-path')
			)

			if (isNewSelection) {
				// The wheel swatch's aria-label is a generic "Wheel N", not the real wheel
				// name, so an explicit value override (from data-wheel-desc) is required.
				// Omit the key entirely (rather than passing `undefined`) when there's no
				// description, so GBAAL's own derivation isn't overridden with nothing.
				dispatchGbaalLinkClickEvent(this, input, {
					...(wheelDescription ? { value: wheelDescription } : {}),
					interactionType: 'change',
				})
			}
		} else {
			window.console.error(`Selected wheel (${selectedWheel}) not found`)
		}
	}

	tileExit(): void {
		this.wheelGroup.remove()
		this.wheelGroup = this.querySelector('.wheel-group')
		this.classList.remove('disable-expand')
		this.removeAttribute('inert')

		// Only restore when inert cleared focus (body/html). If the user tabbed elsewhere
		// during the animation, do not yank focus back.
		const activeEl = document.activeElement
		const focusWasCleared = !activeEl || activeEl === document.body || activeEl === document.documentElement

		if (this.focusRestoreTarget && focusWasCleared) {
			this.focusRestoreTarget.focus({ preventScroll: true })
		}
		this.focusRestoreTarget = null
	}

	/**
	 * Method to change the current active wheel
	 *
	 * @param wheel The wheel to change to/activate
	 */
	activateWheel(
		wheel: string,
		wheelImgSrc: string,
		wheelDescription: string,
		wheelAlt: string,
		wheelDisclosurePath?: string
	): void {
		if (wheel === this.activeWheelValue) {
			return
		}

		this.activeWheelValue = wheel

		/* todo:  need to look at reduced motion
		if (!window.matchMedia(`(prefers-reduced-motion: reduce)`).matches) {
			this.classList.add('disable-expand')
		} */
		this.classList.add('disable-expand')
		this.setAttribute('inert', '')

		const sanitizedDisclosurePath = wheelDisclosurePath ? sanitizeUrl(wheelDisclosurePath) : null
		if (sanitizedDisclosurePath) {
			this.wheelSwatchDescription.textContent = ''
			this.tileDescriptionEl.textContent = ''

			const newDisclosureSpanEl = document.createElement('span')
			newDisclosureSpanEl.setAttribute('data-legal-uri', sanitizedDisclosurePath)
			newDisclosureSpanEl.textContent = wheelDescription
			this.wheelSwatchDescription.appendChild(newDisclosureSpanEl)
			this.tileDescriptionEl.appendChild(newDisclosureSpanEl.cloneNode(true))
		} else {
			this.wheelSwatchDescription.textContent = wheelDescription
			this.tileDescriptionEl.textContent = wheelDescription
		}

		//cleanup previous wheel
		this.wheelGroup.classList.remove('enter-tile')

		// Add new wheel
		const newWheel: HTMLElement = this.wheelGroup.cloneNode(true) as HTMLElement

		this._loadWheelImageFromBlob(wheel, wheelImgSrc, wheelAlt, newWheel)

		this.wheelOverlayImgContainerEl.appendChild(newWheel)

		//animate old wheel out
		this.wheelGroup.classList.add('exit-tile')

		//animate new wheel in
		newWheel.classList.add('enter-tile')

		//remove old wheel after animation
		this.wheelGroup.addEventListener('animationend', this.boundTileExit, {
			once: true,
		})

		this.wheelSwatchEls.forEach(el => {
			if (el.value === wheel) {
				el.checked = true
			}
		})

		this.expandedWheelSwatchEls.forEach(el => {
			if (el.value === wheel) {
				el.checked = true
			}
		})
	}

	private _loadWheelImageFromBlob(
		wheelId: string,
		wheelImgSrc: string,
		wheelAlt: string,
		wheelEl: HTMLElement
	): void {
		const wheelOverlayEl = wheelEl.querySelector('.wheel-overlay') as HTMLImageElement | null
		if (wheelOverlayEl) {
			const blob = this.wheelImgBlobData[wheelId]
			try {
				const imgURL = window.URL.createObjectURL(blob)
				wheelOverlayEl.onload = () => {
					window.URL.revokeObjectURL(imgURL)
				}
				wheelOverlayEl.onerror = () => {
					window.URL.revokeObjectURL(imgURL)
					window.console.error(`ERROR: Wheel Tile.  Failed to load image for wheel ${wheelId}`)
				}

				wheelOverlayEl.src = imgURL
			} catch {
				// Fallback to original URL in case of error
				wheelOverlayEl.src = sanitizeUrl(wheelImgSrc)
			}

			if (wheelAlt != null) {
				wheelOverlayEl.setAttribute('alt', sanitizeInput(wheelAlt))
			} else {
				wheelOverlayEl.removeAttribute('alt')
			}
		}
	}
}

if (!customElements.get('gmx-wheel-tile')) {
	customElements.define('gmx-wheel-tile', GMXWheelTile)
}
